<!--
 * @Description: 移动端首页顶部导航栏
 * @Date: 2026-01-09
-->
<template>
    <header class="mobile-home-header lg:hidden">
        <!-- Logo -->
        <NuxtLink to="/" class="logo">
            <img src="https://static.weryai.com/growth/image/weryai_5cb7d93ce37f69e6825b5bf4396a3d12.png" alt="微鲤AI"
                class="logo-img" />
        </NuxtLink>

        <!-- 右侧操作区 -->
        <div class="actions">
            <!-- 签到（仅登录用户显示） -->
            <button v-if="SIGN_IN_ENABLED && userInfo" class="checkin-btn" @click="handleCheckin" :title="t('menus.checkin')">
                <img src="~/assets/images/common/sign_in.svg" alt="签到" class="checkin-icon" />
                <!-- 红点提示（未签到时显示） -->
                <div v-if="showRedDot" class="checkin-red-dot"></div>
            </button>

            <!-- 积分 -->
            <div class="balance-box" @click="handleBalanceClick">
                <img src="~/assets/images/common/coin.webp" alt="积分" class="coin-icon" />
                <span class="balance-text">{{ formattedBalance }}</span>
            </div>

            <!-- 会员状态 -->
            <button class="vip-btn" :class="vipBtnClass" @click="handleOpenVipModal">
                <span class="vip-text">{{ vipButtonText }}</span>
                <svg class="vip-arrow" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
                <img v-if="vipIconName === 'vip-free'" src="~/assets/images/common/vip-free.png" alt="vip"
                    class="vip-icon" />
                <img v-else-if="vipIconName === 'vip-entry'" src="~/assets/images/common/vip-entry.png" alt="vip"
                    class="vip-icon" />
                <img v-else-if="vipIconName === 'vip-basic'" src="~/assets/images/common/vip-basic.png" alt="vip"
                    class="vip-icon" />
                <img v-else-if="vipIconName === 'vip-pro'" src="~/assets/images/common/vip-pro.png" alt="vip"
                    class="vip-icon" />
            </button>
        </div>
    </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '~/stores/user'
import { useI18n } from '~/composables/useI18n'
import { emitOpenGoodsModal } from '~/utils/emitter'
import { navigateTo } from '#imports'
import { getTodaySignStatusApi } from '@/api/user'
import { SIGN_IN_ENABLED } from '~/utils/feature-flags'

const { t } = useI18n()
const userStore = useUserStore()

// 用户信息
const userInfo = computed(() => userStore.userInfo)

// 签到状态
const signStatus = ref<'NOT_SIGN' | 'SIGNED' | 'NOT_OPEN' | null>(null)

// 是否显示红点 - 只有接口返回 NOT_SIGN 才显示
const showRedDot = computed(() => signStatus.value === 'NOT_SIGN')

// 获取签到状态
const fetchSignStatus = async () => {
    if (!userInfo.value) return
    try {
        const res: any = await getTodaySignStatusApi()
        if (res?.data?.sign_status) {
            signStatus.value = res.data.sign_status
        }
    } catch (error) {
        console.error('获取签到状态失败:', error)
    }
}

onMounted(() => {
    if (SIGN_IN_ENABLED) {
        fetchSignStatus()
    }
})

// 积分余额
const balance = computed(() => userStore.balance || 0)

// 格式化积分（超过1万用w表示）
const formattedBalance = computed(() => {
    const val = balance.value
    if (val >= 10000) {
        return (val / 10000).toFixed(1) + 'w'
    }
    return val.toString()
})

// VIP 信息
const accountSummary = computed(() => userStore.accountSummary)
const vipSubscriptionType = computed(
    () => accountSummary.value?.sub_info?.subscription_type
)

// VIP 按钮文字
const vipButtonText = computed(() => {
    const type = vipSubscriptionType.value
    if (!type) return t('menus.startVip')

    const levelMap: Record<string, string> = {
        PLAN_ENTRY: t('menus.planEntry'),
        PLAN_BASIC: t('menus.planBasic'),
        PLAN_PRO: t('menus.planPro'),
    }
    return levelMap[type] || t('menus.startVip')
})

// VIP 按钮样式类
const vipBtnClass = computed(() => {
    const type = vipSubscriptionType.value
    if (!type) return 'vip-btn-free'

    const classMap: Record<string, string> = {
        PLAN_ENTRY: 'vip-btn-entry',
        PLAN_BASIC: 'vip-btn-basic',
        PLAN_PRO: 'vip-btn-pro',
    }
    return classMap[type] || 'vip-btn-free'
})

// VIP 图标名称
const vipIconName = computed(() => {
    const type = vipSubscriptionType.value
    const iconMap: Record<string, string> = {
        PLAN_ENTRY: 'vip-entry',
        PLAN_BASIC: 'vip-basic',
        PLAN_PRO: 'vip-pro',
    }
    return iconMap[type as string] || 'vip-free'
})

// 签到
const handleCheckin = () => {
    // 如果未登录，跳转到登录
    if (!userInfo.value) {
        import('~/utils/emitter').then(({ showLoginModal }) => {
            showLoginModal()
        })
        return
    }
    // 打开签到弹窗
    import('~/utils/emitter').then(({ emitOpenSignInModal }) => {
        emitOpenSignInModal()
    })
}

// 点击积分
const handleBalanceClick = () => {
    if (!userInfo.value) {
        import('~/utils/emitter').then(({ showLoginModal }) => {
            showLoginModal()
        })
        return
    }

    if (balance.value === 0) {
        // 余额为0，跳转到 pricing 页面
        navigateTo('/pricing?tab=yearly')
    } else {
        // 有余额，跳转到积分页面
        navigateTo('/pricing?tab=credits')
    }
}

// 打开会员弹窗
const handleOpenVipModal = () => {
    if (!userInfo.value) {
        import('~/utils/emitter').then(({ showLoginModal }) => {
            showLoginModal()
        })
        return
    }
    emitOpenGoodsModal()
}
</script>

<style scoped lang="less">
.mobile-home-header {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 52px;
    padding: 0 12px;
    background: rgba(13, 14, 18, 0.95);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);

    // PC 端隐藏（lg: 1024px 及以上）
    @media (min-width: 1024px) {
        display: none !important;
    }
}

.logo {
    flex-shrink: 0;
}

.logo-img {
    height: 40px;
    width: auto;
}

.actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.checkin-btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: transform 0.2s;

    &:active {
        transform: scale(0.9);
    }
}

.checkin-icon {
    width: 32px;
    height: 32px;
    object-fit: contain;
}

.checkin-red-dot {
    position: absolute;
    top: 0;
    right: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #de2c2c;
    border: 1.5px solid rgba(255, 255, 255, 0.9);
    animation: pulse 2s infinite;
}

@keyframes pulse {

    0%,
    100% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(1.1);
        opacity: 0.8;
    }
}

.balance-box {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    cursor: pointer;
    transition: background 0.2s;

    &:active {
        background: rgba(255, 255, 255, 0.12);
    }
}

.coin-icon {
    width: 16px;
    height: 16px;
}

.balance-text {
    font-size: 13px;
    font-weight: 500;
    color: #ffffff;
}

// 会员按钮基础样式
.vip-btn {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 6px 12px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
    border-radius: 100px;

    &:active {
        transform: scale(0.95);
    }
}

// 免费用户 - 绿色主题
.vip-btn-free {
    background: linear-gradient(90deg, #1f321b 0%, #1b2320 50%, #171f1a 100%);



    .vip-text {
        background: linear-gradient(90deg, #feffb5 0%, #99f768 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    .vip-arrow {
        color: #99f768;
    }
}

// 入门会员 (PLAN_ENTRY) - 青蓝色主题
.vip-btn-entry {
    background: linear-gradient(90deg, #20383a 0%, #1b2123 50%, #171c1f 100%);



    .vip-text {
        background: linear-gradient(90deg, #b5ffe6 0%, #68d1f7 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    .vip-arrow {
        color: #68d1f7;
    }
}

// Basic 会员 (PLAN_BASIC) - 金黄色主题
.vip-btn-basic {
    background: linear-gradient(90deg, #393a20 0%, #23201b 50%, #1f1c17 100%);


    .vip-text {
        background: linear-gradient(90deg, #fdffb5 0%, #f7cc68 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    .vip-arrow {
        color: #f7cc68;
    }
}

// Pro 会员 (PLAN_PRO) - 粉红色主题
.vip-btn-pro {
    background: linear-gradient(90deg, #3a2020 0%, #231b1f 50%, #1f171d 100%);



    .vip-text {
        background: linear-gradient(90deg, #ffc0b5 0%, #f76868 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    .vip-arrow {
        color: #f76868;
    }
}

.vip-text {
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
}

.vip-arrow {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
}

.vip-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-left: 2px;
}
</style>
